// Account for each slide's own width and keep navigation in sync.
if (!customElements.get('case-study-gallery')) {
  customElements.define(
    'case-study-gallery',
    class CaseStudyGallery extends HTMLElement {
      connectedCallback() {
        this.slider = this.querySelector('[id^="Slider-"]');
        this.slides = Array.from(this.querySelectorAll('[id^="Slide-"]'));
        if (!this.slider || !this.slides.length) return;

        this.currentIndex = 0;
        this.previousButton = this.querySelector('button[name="previous"]');
        this.nextButton = this.querySelector('button[name="next"]');
        this.counter = this.querySelector('.slider-counter--current');
        this.total = this.querySelector('.slider-counter--total');
        this.events = new AbortController();
        const options = { signal: this.events.signal };

        this.previousButton?.addEventListener('click', () => this.step(-1), options);
        this.nextButton?.addEventListener('click', () => this.step(1), options);
        this.slider.addEventListener('scroll', () => this.update(), { ...options, passive: true });
        this.slider.addEventListener('keydown', (event) => this.onKeyDown(event), options);
        this.slider.addEventListener('pointerdown', (event) => this.startDrag(event), options);
        this.slider.addEventListener('pointermove', (event) => this.drag(event), options);
        this.slider.addEventListener('pointerup', () => this.endDrag(), options);
        this.slider.addEventListener('pointercancel', () => this.endDrag(), options);
        this.slider.addEventListener('lostpointercapture', () => this.endDrag(), options);

        this.resizeObserver = new ResizeObserver(() => this.measure());
        this.resizeObserver.observe(this.slider);
        this.slides.forEach((slide) => this.resizeObserver.observe(slide));
        this.measure();
      }

      disconnectedCallback() {
        this.events?.abort();
        this.resizeObserver?.disconnect();
        this.slider?.classList.remove('is-dragging');
        this.dragStart = null;
      }

      get maximumScroll() {
        return Math.max(0, this.slider.scrollWidth - this.slider.clientWidth);
      }

      measure() {
        const bounds = this.slider.getBoundingClientRect();
        const maximum = this.maximumScroll;
        this.positions = this.slides.map((slide) => {
          const imageBounds = slide.getBoundingClientRect();
          const center = imageBounds.left - bounds.left + this.slider.scrollLeft + imageBounds.width / 2;
          return Math.max(0, Math.min(maximum, center - this.slider.clientWidth / 2));
        });
        // Preserve the active slide when the viewport, embed, or theme editor changes size.
        this.slider.scrollTo({ left: this.positions[this.currentIndex], behavior: 'instant' });
        this.update();
      }

      closestIndex() {
        if (this.slider.scrollLeft <= 1 || this.maximumScroll <= 1) return 0;
        if (this.slider.scrollLeft >= this.maximumScroll - 1) return this.slides.length - 1;
        return this.positions.reduce(
          (closest, position, index) =>
            Math.abs(position - this.slider.scrollLeft) < Math.abs(this.positions[closest] - this.slider.scrollLeft)
              ? index
              : closest,
          0
        );
      }

      update() {
        this.currentIndex = this.closestIndex();
        if (this.counter) this.counter.textContent = this.currentIndex + 1;
        if (this.total) this.total.textContent = this.slides.length;
        if (this.previousButton) this.previousButton.disabled = this.slider.scrollLeft <= 1;
        if (this.nextButton) this.nextButton.disabled = this.slider.scrollLeft >= this.maximumScroll - 1;
      }

      step(direction) {
        // Several small edge slides can share a clamped position. Skip targets that cannot move the gallery.
        const position = this.slider.scrollLeft;
        let target = -1;
        for (let index = 0; index < this.positions.length; index += 1) {
          if (direction > 0 && this.positions[index] > position + 1) {
            target = index;
            break;
          }
          if (direction < 0 && this.positions[index] < position - 1) target = index;
        }
        if (target !== -1) this.goTo(target);
      }

      goTo(index) {
        const target = Math.max(0, Math.min(this.slides.length - 1, index));
        // The theme's CSS handles smooth scrolling and prefers-reduced-motion.
        this.slider.scrollTo({ left: this.positions[target] });
      }

      onKeyDown(event) {
        if (event.target !== this.slider || event.altKey || event.ctrlKey || event.metaKey) return;
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          this.step(event.key === 'ArrowRight' ? 1 : -1);
          return;
        }
        const targets = {
          Home: 0,
          End: this.slides.length - 1,
        };
        if (!(event.key in targets)) return;
        event.preventDefault();
        this.goTo(targets[event.key]);
      }

      startDrag(event) {
        // Touch keeps the browser's native swipe and vertical page scrolling.
        if (event.pointerType === 'touch' || event.button !== 0 || this.slides.length < 2) return;
        // Leave players and links in control of their own clicks.
        if (event.target.closest('case-study-reel, instagram-reels, a, button, input, select, textarea, iframe')) return;
        this.dragStart = { x: event.clientX, left: this.slider.scrollLeft };
        this.slider.setPointerCapture(event.pointerId);
        this.slider.focus({ preventScroll: true });
        event.preventDefault();
      }

      drag(event) {
        if (!this.dragStart) return;
        const distance = event.clientX - this.dragStart.x;
        if (Math.abs(distance) < 4 && !this.slider.classList.contains('is-dragging')) return;
        this.slider.classList.add('is-dragging');
        this.slider.scrollLeft = this.dragStart.left - distance;
      }

      endDrag() {
        if (!this.dragStart) return;
        this.dragStart = null;
        const closest = this.closestIndex();
        this.slider.classList.remove('is-dragging');
        this.goTo(closest);
      }
    }
  );
}
